/**
 * BuilderOverlay — wspólna powłoka okien kreatorów menu (kreator formularza i listy pozycji
 * menu, kreatory kartotek tenanta, widok kartoteki obcej).
 *
 * Wygląd pozostaje „kreatorowy" (prawie pełny ekran, `max-w-7xl` / `max-w-[90vw]`), ale
 * zachowanie daje wspólny rdzeń `Overlay`: zamykanie tłem tylko gdy wciśnięcie i puszczenie
 * myszy były na tle, Esc, pułapka fokusu, blokada przewijania, portal i stos okien.
 *
 * `onRequestClose` to JEDYNA droga zamknięcia (tło, Esc i X): okno samo decyduje, czy najpierw
 * zapytać o niezapisane zmiany (`handleRequestClose` w kreatorach ze strażnikiem). Dlatego
 * powłoka nie używa `guardUnsaved` — pytanie o zmiany jest tam logiką kreatora, nie powłoki.
 */

import { type ReactNode, useId } from 'react';
import { X } from 'lucide-react';

import { IconButton } from '@/components/IconButton';
import { Overlay } from '@/components/Overlay';

export interface BuilderOverlayProps {
  /** Żądanie zamknięcia — woła je tło, Esc i X. */
  onRequestClose: () => void;
  /** `data-testid` na tle okna (zawiera cały panel). */
  testId: string;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Plakietka w nagłówku (np. „Podgląd — tylko do odczytu"). */
  banner?: ReactNode;
  closeLabel: string;
  closeTestId: string;
  /** `wide` = `max-w-7xl`, `full` = `max-w-[90vw]`. */
  width?: 'wide' | 'full';
  /**
   * `false` = tło i Esc NIE zamykają (zamyka tylko X lub przycisk w treści). Używane w dwóch
   * przypadkach: (1) okno bez sygnału niezapisanych zmian (widok kartoteki obcej) — przypadkowe
   * zamknięcie kosztowałoby pracę; (2) na czas zapisu w toku (`dismissible={!saving}`).
   */
  dismissible?: boolean;
  children: ReactNode;
}

const WIDTH_CLASS = { wide: 'max-w-7xl', full: 'max-w-[90vw]' } as const;

export function BuilderOverlay({
  onRequestClose,
  testId,
  title,
  subtitle,
  banner,
  closeLabel,
  closeTestId,
  width = 'wide',
  dismissible = true,
  children,
}: BuilderOverlayProps) {
  const titleId = useId();
  return (
    <Overlay
      open
      onClose={onRequestClose}
      closeOnBackdrop={dismissible}
      closeOnEsc={dismissible}
      backdropClassName="z-50 flex items-center justify-center bg-black/50 p-4 sm:p-8"
      panelClassName={`flex h-[calc(100vh-4rem)] min-h-0 w-full ${WIDTH_CLASS[width]} flex-col rounded-lg bg-bg-primary shadow-xl`}
      labelledBy={titleId}
      testId={testId}
    >
      <header className="flex shrink-0 items-center justify-between border-b border-border-secondary px-6 py-4">
        <div>
          <h2 id={titleId} className="text-lg font-semibold text-text-primary">
            {title}
          </h2>
          {subtitle ? <p className="mt-0.5 text-sm text-text-muted">{subtitle}</p> : null}
        </div>
        <div className="flex items-center gap-3">
          {banner}
          <IconButton onClick={onRequestClose} aria-label={closeLabel} data-testid={closeTestId}>
            <X aria-hidden="true" />
          </IconButton>
        </div>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto p-6">{children}</div>
    </Overlay>
  );
}
